import { useState } from "react";
import { Table, Button, Modal, Form, Input, Select, DatePicker, InputNumber, Popconfirm, message, Space, Tag } from "antd";
import { Plus } from "lucide-react";
import dayjs from "dayjs";
import { useHRUsers, useLeave, useLeaveCreate, useLeaveUpdate, useLeaveDelete } from "../../../../api-hooks/hr";

const LEAVE_TYPES = ["annual","sick","maternity","paternity","adoption","bereavement","unpaid","other"];
const STATUS_COLORS: Record<string, string> = { pending: "orange", approved: "green", rejected: "red", cancelled: "default" };

export default function LeaveTab({ cid }: { cid: number }) {
  const [msgApi, ctx] = message.useMessage();
  const [form] = Form.useForm();
  const [modal, setModal] = useState<{ open: boolean; rec: any }>({ open: false, rec: null });
  const [page, setPage] = useState(1);
  const [filterStatus, setFilterStatus] = useState<string | undefined>();

  const { data: users = [] } = useHRUsers(cid);
  const { data: leaveData, isLoading } = useLeave(cid, { page, limit: 50, status: filterStatus });
  const rows = (leaveData as any)?.data ?? [];
  const total = (leaveData as any)?.total ?? 0;

  const cb = (msg: string, fn?: () => void) => ({ onSuccess: () => { msgApi.success(msg); fn?.(); }, onError: (e: any) => msgApi.error(e?.message || "Error") });
  const create = useLeaveCreate(cid, cb("Leave request created.", () => setModal({ open: false, rec: null })));
  const update = useLeaveUpdate(cid, cb("Updated.", () => setModal({ open: false, rec: null })));
  const del = useLeaveDelete(cid, { onSuccess: () => msgApi.success("Deleted."), onError: (e: any) => msgApi.error(e?.message) });

  const cols = [
    { title: "Employee", dataIndex: "user_name", key: "u" },
    { title: "Type", dataIndex: "leave_type", key: "lt", render: (v: string) => v?.replace(/_/g," ").replace(/\b\w/g, c => c.toUpperCase()) },
    { title: "Start", dataIndex: "start_date", key: "s", render: (v: string) => v ? dayjs(v).format("DD/MM/YY") : "—" },
    { title: "End", dataIndex: "end_date", key: "e", render: (v: string) => v ? dayjs(v).format("DD/MM/YY") : "—" },
    { title: "Days", dataIndex: "days_requested", key: "d" },
    { title: "Status", dataIndex: "status", key: "st", render: (v: string) => <Tag color={STATUS_COLORS[v] ?? "default"}>{v}</Tag> },
    { title: "", key: "act", render: (_: any, r: any) => (
      <Space size="small">
        <Button size="small" onClick={() => { setModal({ open: true, rec: r }); form.setFieldsValue({ ...r, start_date: r.start_date ? dayjs(r.start_date) : null, end_date: r.end_date ? dayjs(r.end_date) : null }); }}>Edit</Button>
        <Popconfirm title="Delete?" onConfirm={() => del.mutate({ id: r.id })} okButtonProps={{ danger: true }}><Button size="small" danger>Del</Button></Popconfirm>
      </Space>
    )},
  ];

  return (
    <div>
      {ctx}
      <div className="flex justify-between mb-3">
        <Select allowClear placeholder="Filter by status" style={{ width: 160 }} onChange={v => { setFilterStatus(v); setPage(1); }} options={["pending","approved","rejected","cancelled"].map(v => ({ value: v, label: v }))} />
        <Button size="small" type="primary" icon={<Plus size={13} />} onClick={() => { setModal({ open: true, rec: null }); form.resetFields(); form.setFieldsValue({ leave_type: "annual" }); }} style={{ background: "#4c1d95" }}>New Request</Button>
      </div>
      <Table rowKey="id" dataSource={rows} columns={cols} loading={isLoading} size="small" pagination={{ current: page, pageSize: 50, total, onChange: setPage }} />
      <Modal open={modal.open} onCancel={() => setModal({ open: false, rec: null })} footer={null} destroyOnClose title={modal.rec ? "Edit Leave Request" : "New Leave Request"} width={480}>
        <Form form={form} layout="vertical" size="middle" className="pt-2" onFinish={(vals) => {
          const p = { ...vals, start_date: vals.start_date?.format("YYYY-MM-DD"), end_date: vals.end_date?.format("YYYY-MM-DD") };
          if (modal.rec) update.mutate({ id: modal.rec.id, ...p }); else create.mutate(p);
        }}>
          <Form.Item name="user_id" label="Employee" rules={[{ required: true }]}><Select options={(users as any[])?.map(u => ({ value: u.id, label: u.name }))} showSearch optionFilterProp="label" /></Form.Item>
          <Form.Item name="leave_type" label="Leave Type" rules={[{ required: true }]}><Select options={LEAVE_TYPES.map(v => ({ value: v, label: v.replace(/_/g," ").replace(/\b\w/g, c => c.toUpperCase()) }))} /></Form.Item>
          <div className="grid grid-cols-3 gap-x-3">
            <Form.Item name="start_date" label="Start" rules={[{ required: true }]}><DatePicker className="w-full" format="DD/MM/YYYY" /></Form.Item>
            <Form.Item name="end_date" label="End" rules={[{ required: true }]}><DatePicker className="w-full" format="DD/MM/YYYY" /></Form.Item>
            <Form.Item name="days_requested" label="Days" rules={[{ required: true }]}><InputNumber className="w-full" min={0.5} step={0.5} /></Form.Item>
          </div>
          {modal.rec && (
            <>
              <Form.Item name="status" label="Status"><Select options={["pending","approved","rejected","cancelled"].map(v => ({ value: v, label: v }))} /></Form.Item>
              <Form.Item name="decision_note" label="Decision Note"><Input.TextArea rows={2} /></Form.Item>
            </>
          )}
          <Form.Item name="reason" label="Reason"><Input.TextArea rows={2} /></Form.Item>
          <div className="flex gap-2 pt-1">
            <Button type="primary" htmlType="submit" style={{ background: "#4c1d95" }} loading={create.isPending || update.isPending}>Save</Button>
            <Button onClick={() => setModal({ open: false, rec: null })}>Cancel</Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
}
